import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type GridColumn,
} from '@/components/desktop';
import { PickList, type PickItem } from '@/components/PickList';
import { SearchSelect, type SearchOption } from '@/components/SearchSelect';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  SearchBar,
  TextArea,
  errorMessage,
  shortDate,
  todayIso,
} from '@/components/ui';
import { useAuth } from '@/stores/auth';
import { confirmAction } from '@/stores/confirm';
import { toast } from '@/stores/toast';
import { describeCode, useRegistry } from './PermissionGrid';

type HelperRow = components['schemas']['HelperListItemDto'];
type HelperDetail = components['schemas']['HelperDetailDto'];
type UserRow = components['schemas']['UserListItemDto'];
type GivableRole = components['schemas']['HelperRoleDto'];
type Reach = components['schemas']['HelperReachDto'];

/**
 * Access Helpers (.docs/027): the people the Managing Director lets change
 * other people's access, and the limits each one works inside.
 *
 * The limits are the whole point, so the form is a list of choices rather
 * than a free hand: these people (by name or by farm), these roles and these
 * single permissions. What may never be handed over — anything about access
 * itself, about pay, or that makes money final — is not offered at all, and
 * the server refuses it a second time.
 */
export function AccessHelpers() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<'appoint' | 'edit' | null>(null);

  const list = useQuery({
    queryKey: ['access-helpers'],
    queryFn: () => api.get('/api/v1/access-helpers'),
  });

  const rows = useMemo(() => {
    const all = (list.data ?? []) as HelperRow[];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (h) =>
        h.fullName.toLowerCase().includes(needle) || h.handle.toLowerCase().includes(needle),
    );
  }, [list.data, search]);

  const selected = rows.find((h) => h.helperId === selectedKey);
  const canEdit = can('iam.helper.update');

  const end = useMutation({
    mutationFn: (helperId: string) =>
      api.delete<components['schemas']['HelperRemovedDto']>(
        `/api/v1/access-helpers/${helperId}` as '/api/v1/access-helpers/{id}',
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['access-helpers'] });
      setSelectedKey(null);
      toast.success(result.message ?? 'That delegation has ended.');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not end it')),
  });

  const columns: Array<GridColumn<HelperRow>> = [
    { key: 'name', header: 'Helper', width: 200, render: (h) => h.fullName },
    { key: 'handle', header: 'User ID', width: 140, render: (h) => <span className="mono">{h.handle}</span> },
    {
      key: 'reaches',
      header: 'Looks after',
      align: 'end',
      width: 110,
      render: (h) => peopleText(h.reaches),
    },
    {
      key: 'gives',
      header: 'May give',
      width: 190,
      render: (h) => (
        <span className="dim">
          {[
            h.roleCount ? `${h.roleCount} role${h.roleCount === 1 ? '' : 's'}` : null,
            h.permissionCount
              ? `${h.permissionCount} permission${h.permissionCount === 1 ? '' : 's'}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ') || '—'}
        </span>
      ),
    },
    {
      key: 'until',
      header: 'Until',
      width: 120,
      render: (h) => (h.expiresAt ? shortDate(h.expiresAt) : <span className="dim">No end date</span>),
    },
    {
      key: 'status',
      header: 'Status',
      width: 130,
      render: (h) =>
        h.inForce ? (
          <span className="pill">In force</span>
        ) : (
          <span className="pill pill--warn">{whyNotInForce(h)}</span>
        ),
    },
  ];

  const toolbarActions: DesktopAction[] = canEdit
    ? [
        { label: 'Appoint a helper', icon: 'add', onSelect: () => setForm('appoint') },
        ...(selected
          ? [
              {
                label: 'Change what they may do',
                icon: 'edit' as const,
                onSelect: () => setForm('edit'),
              },
              {
                label: 'End this delegation',
                icon: 'remove' as const,
                onSelect: () => {
                  void confirmAction({
                    title: `End ${selected.fullName}'s delegation?`,
                    message: 'They stop being able to change anyone’s access at once. Nothing they have already done is undone.',
                    confirmLabel: 'End it',
                  }).then((ok) => ok && end.mutate(selected.helperId));
                },
              },
            ]
          : []),
      ]
    : [];

  if (form) {
    return (
      <HelperForm
        helperId={form === 'edit' && selected ? selected.helperId : null}
        onCancel={() => setForm(null)}
        onSaved={(message) => {
          setForm(null);
          void queryClient.invalidateQueries({ queryKey: ['access-helpers'] });
          toast.success(message);
        }}
      />
    );
  }

  return (
    <div className="workbench">
      <SplitView
        id="iam-helpers"
        direction="column"
        initial={340}
        firstLabel="helper list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <SearchBar value={search} onChange={setSearch} placeholder="Search helpers by name or user ID…" />
            </div>

            <ActionBar actions={toolbarActions} />

            {list.isError && <ErrorNote message={errorMessage(list.error, 'Could not load the helpers')} />}

            <DataGrid
              id="iam-helpers"
              label="Access helpers"
              columns={columns}
              rows={rows}
              rowKey={(h) => h.helperId}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onOpen={(h) => {
                setSelectedKey(h.helperId);
                if (canEdit) setForm('edit');
              }}
              loading={list.isPending}
              empty={
                search.trim()
                  ? 'No helper matches your search.'
                  : 'Nobody helps with access yet. Appoint someone to let them change access for the people you choose.'
              }
            />
          </div>
        }
        second={
          <DetailPane
            tabs={
              selected
                ? [
                    {
                      id: 'details',
                      label: 'Details',
                      icon: 'details',
                      content: <HelperSummary helperId={selected.helperId} />,
                    },
                  ]
                : []
            }
            activeTab="details"
            onTabChange={() => {}}
            empty="Choose a helper to see who they look after."
            title={selected && <span>{selected.fullName}</span>}
          />
        }
      />
      <StatusBar>
        <span>
          {rows.length} helper{rows.length === 1 ? '' : 's'}
        </span>
        <span>{selected ? `Chosen: ${selected.fullName}` : 'No helper chosen'}</span>
      </StatusBar>
    </div>
  );
}

function peopleText(n: number): string {
  return n === 1 ? '1 person' : `${n} people`;
}

/** Why a delegation is not working right now, in a few words. */
function whyNotInForce(h: HelperRow): string {
  if (h.helperStatus !== 'ACTIVE') return 'Helper switched off';
  if (!h.isActive) return 'Paused';
  if (h.expiresAt && new Date(h.expiresAt) <= new Date()) return 'Ended';
  return 'Not in force';
}

function useHelper(helperId: string | null) {
  return useQuery({
    queryKey: ['access-helpers', helperId],
    queryFn: () =>
      api.get(`/api/v1/access-helpers/${helperId}` as '/api/v1/access-helpers/{id}'),
    enabled: helperId !== null,
  });
}

/** Who one helper looks after, and what they may hand out. */
function HelperSummary({ helperId }: { helperId: string }) {
  const detail = useHelper(helperId);
  const registry = useRegistry();
  const h = detail.data as HelperDetail | undefined;
  if (detail.isError) return <ErrorNote message={errorMessage(detail.error, 'Could not load it')} />;
  if (!h) return <p className="muted">Loading…</p>;

  return (
    <div className="stack">
      <dl className="detaillist">
        <dt>Why</dt>
        <dd>{h.reason}</dd>
        <dt>May add people</dt>
        <dd>{h.canAddUsers ? 'Yes, on their farms and with their roles' : 'No'}</dd>
        <dt>Until</dt>
        <dd>{h.expiresAt ? shortDate(h.expiresAt) : 'No end date'}</dd>
      </dl>

      <section>
        <h3 className="field__label">People by name</h3>
        <p>{h.people.length === 0 ? <span className="dim">None</span> : h.people.map((p) => p.fullName).join(', ')}</p>
      </section>

      <section>
        <h3 className="field__label">Everyone on these farms</h3>
        <p>
          {h.farms.length === 0 ? (
            <span className="dim">None</span>
          ) : (
            h.farms.map((f) => `${f.code} ${f.nameEn}`).join(', ')
          )}
        </p>
      </section>

      <section>
        <h3 className="field__label">Roles they may give</h3>
        <p className="settings__choices">
          {h.roles.length === 0 ? (
            <span className="dim">None</span>
          ) : (
            h.roles.map((r) => (
              <span key={r.id} className={`pill${r.undelegable.length ? ' pill--warn' : ''}`}>
                {r.nameEn}
                {r.undelegable.length > 0 && ' — now includes things only you give'}
              </span>
            ))
          )}
        </p>
      </section>

      <section>
        <h3 className="field__label">Single permissions they may give</h3>
        <ul className="plainlist">
          {h.permissions.length === 0 ? (
            <li className="dim">None</li>
          ) : (
            h.permissions.map((code) => (
              <li key={code}>{describeCode(registry.data ?? [], code).text}</li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}

/** The four things the MD decides, in the order they decide them. */
const STEPS = [
  { id: 'who', title: 'Who helps' },
  { id: 'after', title: 'Who they look after' },
  { id: 'give', title: 'What they may give' },
  { id: 'why', title: 'Why, and for how long' },
  { id: 'review', title: 'Check and appoint' },
] as const;

type StepId = (typeof STEPS)[number]['id'];

/** What one helper may do, as the form holds it while being filled in. */
interface Draft {
  helper: string;
  people: string[];
  farms: string[];
  roles: string[];
  permissions: string[];
  canAddUsers: boolean;
  reason: string;
  expiresAt: string;
}

const EMPTY: Draft = {
  helper: '',
  people: [],
  farms: [],
  roles: [],
  permissions: [],
  canAddUsers: false,
  reason: '',
  expiresAt: '',
};

function draftOf(h: HelperDetail): Draft {
  return {
    helper: h.helperId,
    people: h.people.map((p) => p.id),
    farms: h.farms.map((f) => f.id),
    roles: h.roles.map((r) => r.id),
    permissions: h.permissions,
    canAddUsers: h.canAddUsers,
    reason: h.reason,
    expiresAt: h.expiresAt ? String(h.expiresAt).slice(0, 10) : '',
  };
}

/**
 * What is wrong with one step, in plain words, or null when it is done. Each
 * step is judged on its own so "Next" can stop at the step that is wrong,
 * rather than the whole form failing at the end on whichever issue came
 * first in one long chain.
 */
function stepProblem(step: StepId, d: Draft): string | null {
  if (step === 'who' && !d.helper) return 'Choose who is helping.';
  if (step === 'after' && d.people.length === 0 && d.farms.length === 0) {
    return 'Choose the people they look after, by name or by farm.';
  }
  if (step === 'give' && d.roles.length === 0 && d.permissions.length === 0) {
    return 'Choose what they may give: at least one role, or one permission.';
  }
  if (step === 'why') {
    if (!d.reason.trim()) return 'Say why they are being appointed. It stays with the record.';
    if (d.expiresAt && d.expiresAt < todayIso()) {
      return 'That end date has already passed. Choose one still to come, or leave it empty.';
    }
  }
  return null;
}

/** The first step that is not done, for jumping straight to what is missing. */
function firstProblemStep(d: Draft): StepId | null {
  for (const s of STEPS) {
    if (s.id !== 'review' && stepProblem(s.id, d)) return s.id;
  }
  return null;
}

/** Appointing a helper, or changing what an existing one may do. */
function HelperForm({
  helperId,
  onCancel,
  onSaved,
}: {
  helperId: string | null;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const editing = helperId !== null;
  const { user } = useAuth();
  const detail = useHelper(helperId);
  const existing = detail.data as HelperDetail | undefined;

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get('/api/v1/users') });
  const farms = useQuery({ queryKey: ['farms'], queryFn: () => api.get('/api/v1/farms') });
  const helpers = useQuery({
    queryKey: ['access-helpers'],
    queryFn: () => api.get('/api/v1/access-helpers'),
  });
  const givableRoles = useQuery({
    queryKey: ['access-helpers', 'givable-roles'],
    queryFn: () => api.get('/api/v1/access-helpers/givable-roles'),
  });
  const registry = useRegistry();

  const [draft, setDraft] = useState<Draft>(EMPTY);
  // Editing opens on the review step: changing one thing should not mean
  // walking through four screens that are already right.
  const [step, setStep] = useState<StepId>(editing ? 'review' : 'who');
  const [filledFrom, setFilledFrom] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [movedPeople, setMovedPeople] = useState<number>(0);

  const set = (patch: Partial<Draft>) => {
    setProblem(null);
    setDraft((d) => ({ ...d, ...patch }));
  };

  // Fill from the delegation as it stands, and again if a different helper is
  // opened without the form being unmounted in between. Adjusted while
  // rendering rather than in an effect: React re-runs this render before
  // showing anything, so the form never appears empty and fills a moment
  // later. `filledFrom` is what stops it from running again every render.
  if (existing && filledFrom !== existing.helperId) {
    setDraft(draftOf(existing));
    setFilledFrom(existing.helperId);
    setStep('review');
  }

  // Held steady so the lists below are rebuilt when the data changes, not on
  // every render: `?? []` would otherwise hand back a new array each time,
  // and these map over hundreds of rows.
  const allUsers = useMemo(() => (users.data ?? []) as UserRow[], [users.data]);
  const roleRows = useMemo(() => (givableRoles.data ?? []) as GivableRole[], [givableRoles.data]);
  const helperRows = (helpers.data ?? []) as HelperRow[];

  const helperName =
    allUsers.find((u) => u.id === draft.helper)?.fullName ?? existing?.fullName ?? '';

  // Appointing someone who already helps silently becomes an edit, because
  // the server keys one delegation per helper. Say so before it happens.
  const alreadyHelps =
    !editing && draft.helper ? helperRows.find((h) => h.helperId === draft.helper) : undefined;

  /** Enough to tell two people of the same name apart, and to search by. */
  const personItem = (u: UserRow): PickItem => ({
    value: u.id,
    label: u.fullName,
    detail: u.userId,
    keywords: [u.fullNameUr ?? '', ...(u.roleNames ?? []), ...(u.farms ?? []).map((f) => f.code)]
      .filter(Boolean)
      .join(' '),
  });

  const helperOptions: SearchOption[] = useMemo(
    () =>
      allUsers
        .filter((u) => u.status === 'ACTIVE' && u.id !== user?.id)
        .map((u) => ({
          value: u.id,
          label: u.fullName,
          meta: u.userId,
          keywords: [u.fullNameUr ?? '', ...(u.roleNames ?? [])].filter(Boolean).join(' '),
        })),
    [allUsers, user?.id],
  );

  const peopleItems = useMemo(
    () => allUsers.filter((u) => u.id !== draft.helper).map(personItem),
    [allUsers, draft.helper],
  );

  const farmItems: PickItem[] = useMemo(
    () => (farms.data ?? []).map((f) => ({ value: f.id, label: `${f.code} — ${f.nameEn}` })),
    [farms.data],
  );

  // A role that has grown to hold something only the MD gives cannot be
  // handed over at all, so it is listed with the reason rather than offered
  // and refused on save.
  const roleItems: PickItem[] = useMemo(
    () =>
      roleRows.map((r) => ({
        value: r.id,
        label: r.undelegable.length > 0 ? `${r.nameEn} — only you may give this` : r.nameEn,
        detail: r.code,
      })),
    [roleRows],
  );
  const barredRoles = useMemo(
    () => new Set(roleRows.filter((r) => r.undelegable.length > 0).map((r) => r.id)),
    [roleRows],
  );

  const permissionItems: PickItem[] = useMemo(() => {
    const modules = registry.data ?? [];
    return modules.flatMap((m) =>
      m.resources.flatMap((r) =>
        r.actionDetails
          .filter((a) => a.delegable)
          .map((a) => ({
            value: a.code,
            label: `${m.label} › ${r.label} › ${a.label}`,
            detail: a.code,
            keywords: a.code,
          })),
      ),
    );
  }, [registry.data]);

  // Who the delegation would reach, answered by the server with the same
  // query that decides it for real, so the number shown is the number meant.
  const reachReady = Boolean(draft.helper) && (draft.people.length > 0 || draft.farms.length > 0);
  const reach = useQuery({
    queryKey: ['access-helpers', 'preview', draft.helper, draft.people, draft.farms],
    queryFn: () =>
      api.post('/api/v1/access-helpers/preview', {
        helperId: draft.helper,
        people: draft.people,
        farms: draft.farms,
      }),
    enabled: reachReady,
    placeholderData: keepPreviousData,
  });
  const reached = reach.data as Reach | undefined;

  const save = useMutation({
    mutationFn: async () => {
      await api.put(`/api/v1/access-helpers/${draft.helper}` as '/api/v1/access-helpers/{id}', {
        people: draft.people,
        farms: draft.farms,
        roles: draft.roles,
        permissions: draft.permissions,
        canAddUsers: draft.canAddUsers,
        reason: draft.reason.trim(),
        ...(draft.expiresAt ? { expiresAt: draft.expiresAt } : {}),
        ...(existing ? { version: existing.version } : {}),
      });
      return helperName;
    },
    onSuccess: (name) =>
      onSaved(
        editing || alreadyHelps
          ? `What ${name} may do has been changed.`
          : `${name} can now help with access.`,
      ),
  });

  // Someone else saved first. Retrying with the version in hand can only fail
  // again, so the way out is to read theirs and start from it.
  const staleVersion = save.error instanceof Error && /changed|version/i.test(save.error.message);

  function chooseHelper(id: string) {
    // A helper cannot look after themselves, and the server refuses it. If
    // they were already ticked as one of the people, take them back out.
    const without = draft.people.filter((p) => p !== id);
    setMovedPeople(draft.people.length - without.length);
    set({ helper: id, people: without });
  }

  function next() {
    const issue = stepProblem(step, draft);
    setProblem(issue);
    if (issue) return;
    const at = STEPS.findIndex((s) => s.id === step);
    setStep(STEPS[Math.min(at + 1, STEPS.length - 1)]!.id);
  }

  function back() {
    const at = STEPS.findIndex((s) => s.id === step);
    setProblem(null);
    setStep(STEPS[Math.max(at - 1, 0)]!.id);
  }

  function submit() {
    const bad = firstProblemStep(draft);
    if (bad) {
      setStep(bad);
      setProblem(stepProblem(bad, draft));
      return;
    }
    setProblem(null);
    save.mutate();
  }

  const reviewing = step === 'review';

  return (
    <FormPanel
      title={editing ? `What ${helperName} may do` : 'Appoint an access helper'}
      intro="A helper changes access for the people you choose, and only in the ways you choose. Anything about access itself, about pay, or that makes money final stays with you."
    >
      {/* The steps check themselves, so a missing answer is named in the
          step's own words rather than left to a browser bubble that says
          "Please fill in this field" over a box three steps back. */}
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (reviewing) submit();
          else next();
        }}
      >
        <StepTrail
          current={step}
          draft={draft}
          onGo={(id) => {
            setProblem(null);
            setStep(id);
          }}
        />

        {step === 'who' && (
          <FormGroup title="Who helps" card>
            {editing ? (
              <Field
                label="Helper"
                value={helperName}
                onChange={() => {}}
                disabled
                hint="Appoint someone else to change a different person"
              />
            ) : (
              <>
                <SearchSelect
                  label="Who helps"
                  value={draft.helper}
                  onChange={(o) => chooseHelper(o?.value ?? '')}
                  options={helperOptions}
                  placeholder={`Search ${helperOptions.length} people by name or user ID…`}
                  selectedLabel={helperName}
                  required
                  clearable
                  clearButton
                  commitExact
                  autoFocus
                  emptyText={(q) => `Nobody active matches “${q}”.`}
                  renderOption={(o) => (
                    <span className="picklist__text">
                      <span className="picklist__label">{o.label}</span>{' '}
                      <span className="picklist__detail mono">{o.meta}</span>
                    </span>
                  )}
                  hint="Only people who are switched on, and never yourself."
                />
                {movedPeople > 0 && (
                  <p className="settings__hint">
                    They were one of the people looked after, so they have been taken off that
                    list: nobody can change their own access.
                  </p>
                )}
                {alreadyHelps && (
                  <p className="settings__hint">
                    {alreadyHelps.fullName} already helps with access. Going on will change what
                    they may do, not appoint them a second time.
                  </p>
                )}
              </>
            )}
          </FormGroup>
        )}

        {step === 'after' && (
          <FormGroup title="Who they look after" card>
            <div className="settings__control">
              <span className="field__label">People by name</span>
              <PickList
                label="People looked after"
                items={peopleItems}
                value={draft.people}
                onChange={(people) => set({ people })}
                noun={{ one: 'person', many: 'people' }}
                searchPlaceholder={`Search ${peopleItems.length} people by name, user ID, role or farm`}
                max={200}
                unknownLabel="Somebody no longer listed"
              />
            </div>

            <div className="settings__control">
              <span className="field__label">Everyone on these farms</span>
              <PickList
                label="Farms looked after"
                items={farmItems}
                value={draft.farms}
                onChange={(farms) => set({ farms })}
                noun={{ one: 'farm', many: 'farms' }}
                searchPlaceholder={`Search ${farmItems.length} farms by code or name`}
                max={200}
                unknownLabel="A farm you cannot see"
                wide
              />
              <p className="settings__hint">
                A farm covers whoever works there later as well, which a list of names would not.
              </p>
            </div>

            <ReachNote reach={reached} ready={reachReady} failed={reach.isError} />
          </FormGroup>
        )}

        {step === 'give' && (
          <FormGroup title="What they may give" card>
            <div className="settings__control">
              <span className="field__label">Roles</span>
              <PickList
                label="Roles they may give"
                items={roleItems}
                value={draft.roles}
                onChange={(roles) => set({ roles })}
                noun={{ one: 'role', many: 'roles' }}
                searchPlaceholder={`Search ${roleItems.length} roles by name or code`}
                max={50}
                unknownLabel="A role no longer in use"
                blocked={barredRoles}
                wide
              />
              <p className="settings__hint">
                A role that later grows to include something only you give stops being giveable at
                once, without anyone editing this.
              </p>
            </div>

            <div className="settings__control">
              <span className="field__label">Single permissions</span>
              <PickList
                label="Permissions they may give"
                items={permissionItems}
                value={draft.permissions}
                onChange={(permissions) => set({ permissions })}
                noun={{ one: 'permission', many: 'permissions' }}
                searchPlaceholder={`Search ${permissionItems.length} permissions`}
                max={200}
                unknownLabel="A permission no longer in use"
                wide
              />
              <p className="settings__hint">Only what may be handed over is listed here.</p>
            </div>

            <label className="settings__toggle">
              <input
                type="checkbox"
                checked={draft.canAddUsers}
                onChange={(e) => set({ canAddUsers: e.target.checked })}
              />
              <span className="field__label">
                May add new people, on their farms and with their roles
              </span>
            </label>
          </FormGroup>
        )}

        {step === 'why' && (
          <FormGroup title="Why, and for how long" columns={2} card>
            <TextArea
              label="Why"
              required
              value={draft.reason}
              onChange={(reason) => set({ reason })}
              span={2}
              rows={3}
              hint={`Kept with the record, as for a personal extra. ${draft.reason.trim().length} of 500 characters.`}
            />
            <Field
              label="Until"
              type="date"
              value={draft.expiresAt}
              onChange={(expiresAt) => set({ expiresAt })}
              min={todayIso()}
              hint="Leave empty for no end date. It ends by itself on this day."
            />
          </FormGroup>
        )}

        {reviewing && (
          <Review
            draft={draft}
            helperName={helperName}
            reach={reached}
            reachReady={reachReady}
            reachFailed={reach.isError}
            roles={roleRows}
            registry={registry.data ?? []}
            onGo={(id) => {
              setProblem(null);
              setStep(id);
            }}
          />
        )}

        {problem && <ErrorNote message={problem} />}
        {save.isError && (
          <>
            <ErrorNote message={errorMessage(save.error, 'Could not save')} />
            {staleVersion && (
              <p className="settings__hint">
                <button
                  type="button"
                  className="linkbutton"
                  onClick={() => {
                    setFilledFrom(null);
                    save.reset();
                    void detail.refetch();
                  }}
                >
                  Read what they saved and start from that
                </button>
              </p>
            )}
          </>
        )}

        <FormActions onCancel={onCancel}>
          {step !== STEPS[0]!.id && (
            <button type="button" className="btn" onClick={back}>
              Back
            </button>
          )}
          {reviewing ? (
            <button type="submit" className="btn btn--primary" disabled={save.isPending}>
              {editing || alreadyHelps ? 'Save changes' : 'Appoint'}
            </button>
          ) : (
            <button type="submit" className="btn btn--primary">
              Next
            </button>
          )}
        </FormActions>
      </form>
    </FormPanel>
  );
}

/** The numbered steps, with every finished one a way back to itself. */
function StepTrail({
  current,
  draft,
  onGo,
}: {
  current: StepId;
  draft: Draft;
  onGo: (id: StepId) => void;
}) {
  const at = STEPS.findIndex((s) => s.id === current);
  return (
    <ol className="steptrail" aria-label="Steps">
      {STEPS.map((s, i) => {
        const done = i < at && !stepProblem(s.id, draft);
        const now = s.id === current;
        return (
          <li
            key={s.id}
            className={`steptrail__step${now ? ' steptrail__step--on' : ''}${done ? ' steptrail__step--done' : ''}`}
          >
            <button
              type="button"
              className="steptrail__btn"
              aria-current={now ? 'step' : undefined}
              onClick={() => onGo(s.id)}
            >
              <span className="steptrail__num" aria-hidden="true">
                {done ? '✓' : i + 1}
              </span>
              <span className="steptrail__label">{s.title}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The first few reached, named so they can be told apart. A name shared by
 * two of them says nothing on its own — "Access admin, Access admin, Access
 * admin" is the very confusion this screen exists to end — so a repeated
 * name carries its user ID.
 */
function sampleNames(reach: Reach): string {
  const seen = new Map<string, number>();
  for (const p of reach.sample) seen.set(p.fullName, (seen.get(p.fullName) ?? 0) + 1);
  const names = reach.sample.map((p) =>
    (seen.get(p.fullName) ?? 0) > 1 ? `${p.fullName} (${p.handle})` : p.fullName,
  );
  const rest = reach.total - reach.sample.length;
  return rest > 0 ? `${names.join(', ')} and ${rest} more` : names.join(', ');
}

/** How far the delegation reaches, as the server works it out. */
function ReachNote({
  reach,
  ready,
  failed,
}: {
  reach: Reach | undefined;
  ready: boolean;
  failed: boolean;
}) {
  if (!ready) {
    return <p className="settings__hint">Choose a person or a farm to see who this reaches.</p>;
  }
  if (failed) {
    return (
      <p className="settings__hint">
        Could not work out who this reaches. The choice is still saved as you made it.
      </p>
    );
  }
  if (!reach) return <p className="settings__hint">Working out who this reaches…</p>;

  return (
    <p className="settings__hint" role="status">
      <strong>{peopleText(reach.total)}</strong> in all.
      {reach.viaFarmOnly > 0 && ` ${reach.viaFarmOnly} of them through a farm rather than by name, so the number grows as people join those farms.`}
      {reach.suspended > 0 && ` ${reach.suspended} ${reach.suspended === 1 ? 'is' : 'are'} switched off.`}
      {reach.sample.length > 0 && <> {sampleNames(reach)}.</>}
    </p>
  );
}

/** The whole delegation in a few sentences, before it is handed over. */
function Review({
  draft,
  helperName,
  reach,
  reachReady,
  reachFailed,
  roles,
  registry,
  onGo,
}: {
  draft: Draft;
  helperName: string;
  reach: Reach | undefined;
  reachReady: boolean;
  reachFailed: boolean;
  roles: GivableRole[];
  registry: Parameters<typeof describeCode>[0];
  onGo: (id: StepId) => void;
}) {
  const chosenRoles = roles.filter((r) => draft.roles.includes(r.id));
  const who = helperName || 'This person';

  return (
    <FormGroup title="Check and appoint" card>
      <p className="review__lede">
        <strong>{who}</strong> will be able to change access for{' '}
        <strong>
          {reachFailed || !reachReady ? 'the people chosen below' : peopleText(reach?.total ?? 0)}
        </strong>
        {draft.farms.length > 0 && reach && reach.viaFarmOnly > 0 && (
          <> ({draft.people.length} by name, {reach.viaFarmOnly} through {draft.farms.length === 1 ? 'a farm' : `${draft.farms.length} farms`})</>
        )}
        . They may give{' '}
        <strong>
          {draft.roles.length} {draft.roles.length === 1 ? 'role' : 'roles'}
        </strong>{' '}
        and{' '}
        <strong>
          {draft.permissions.length}{' '}
          {draft.permissions.length === 1 ? 'single permission' : 'single permissions'}
        </strong>
        {draft.canAddUsers && ', and may add new people'}.{' '}
        {draft.expiresAt
          ? `This ends by itself on ${shortDate(draft.expiresAt)}.`
          : 'This has no end date.'}
      </p>

      <dl className="detaillist">
        <dt>Why</dt>
        <dd>
          {draft.reason.trim() || <span className="dim">Not said yet</span>}{' '}
          <button type="button" className="linkbutton" onClick={() => onGo('why')}>
            Change
          </button>
        </dd>

        <dt>People by name</dt>
        <dd>
          {draft.people.length === 0 ? <span className="dim">None</span> : peopleText(draft.people.length)}{' '}
          <button type="button" className="linkbutton" onClick={() => onGo('after')}>
            Change
          </button>
        </dd>

        <dt>Farms</dt>
        <dd>
          {draft.farms.length === 0 ? (
            <span className="dim">None</span>
          ) : (
            `${draft.farms.length} ${draft.farms.length === 1 ? 'farm' : 'farms'}`
          )}{' '}
          <button type="button" className="linkbutton" onClick={() => onGo('after')}>
            Change
          </button>
        </dd>

        <dt>Roles they may give</dt>
        <dd>
          {chosenRoles.length === 0 ? (
            <span className="dim">None</span>
          ) : (
            <span className="settings__choices">
              {chosenRoles.map((r) => (
                <span key={r.id} className="pill">
                  {r.nameEn}
                </span>
              ))}
            </span>
          )}{' '}
          <button type="button" className="linkbutton" onClick={() => onGo('give')}>
            Change
          </button>
        </dd>

        <dt>Single permissions</dt>
        <dd>
          {draft.permissions.length === 0 ? (
            <span className="dim">None</span>
          ) : (
            <ul className="plainlist">
              {draft.permissions.slice(0, 8).map((code) => (
                <li key={code}>{describeCode(registry, code).text}</li>
              ))}
              {draft.permissions.length > 8 && (
                <li className="dim">and {draft.permissions.length - 8} more</li>
              )}
            </ul>
          )}{' '}
          <button type="button" className="linkbutton" onClick={() => onGo('give')}>
            Change
          </button>
        </dd>
      </dl>

      <ReachNote reach={reach} ready={reachReady} failed={reachFailed} />
    </FormGroup>
  );
}
